import React, { forwardRef } from "react";

import { Field, getIn, FieldProps } from "formik";
import { dissoc } from "ramda";

import { Checkbox, CheckboxProps } from "src/components/Checkbox";

export interface FormikCheckboxProps extends Omit<CheckboxProps, "name"> {
  /** Formik field name. */
  name: string;
  /** Callback fired when the checked state changes. Also supports onChange for compatibility. */
  onChange?: () => void;
}

const FormikCheckbox = forwardRef<
  React.ComponentRef<typeof Checkbox>,
  FormikCheckboxProps
>(
  (
    {
      name,
      onCheckedChange: onCheckedChangeProp,
      onChange: onChangeProp,
      ...rest
    },
    ref
  ) => (
    <Field name={name}>
      {({ field, meta, form }: FieldProps) => {
        const { status = {}, setStatus } = form;
        const fieldStatus = getIn(status, name);

        return (
          <Checkbox
            ref={ref}
            checked={!!field.value}
            error={meta.touched ? meta.error || fieldStatus : ""}
            name={field.name}
            onBlur={field.onBlur}
            onCheckedChange={(checked: boolean) => {
              setStatus(dissoc(name, status));
              form.setFieldValue(name, checked);
              onCheckedChangeProp?.(checked);
              onChangeProp?.();
            }}
            {...rest}
          />
        );
      }}
    </Field>
  )
);

FormikCheckbox.displayName = "FormikCheckbox";

export { FormikCheckbox };
